/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

import {Directive, input, untracked} from '@angular/core';

import {submit} from '../api/structure';
import {FieldState, FieldTree} from '../api/types';
import {FieldNode} from '../field/node';

/**
 * A directive that binds a `FieldTree` to a `<form>` element.
 *
 * It automatically:
 * 1. Sets `novalidate` on the form element to disable browser validation.
 * 2. Listens for the `submit` event, prevents the default behavior, and calls `submit()` on the
 * `FieldTree` if it defines its own submission options.
 *
 * @usageNotes
 *
 * ```html
 * <form [formRoot]="myFieldTree">
 *   ...
 * </form>
 * ```
 *
 * @publicApi 22.0
 */
@Directive({
  selector: 'form[formRoot]',
  host: {
    'novalidate': '',
    '(submit)': 'onSubmit($event)',
  },
})
export class FormRoot<T> {
  readonly fieldTree = input.required<FieldTree<T>>({alias: 'formRoot'});

  protected onSubmit(event: Event): void {
    event.preventDefault();

    untracked(() => {
      const fieldTree = this.fieldTree();
      const node = fieldTree() as FieldState<unknown> as FieldNode;

      if (node.structure.fieldManager.submitOptions) {
        submit(fieldTree);
      }
    });
  }
}
